import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useAuthStore } from '../../stores/authStore'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'
import {
  Users,
  Calendar,
  FileText,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Bell,
  ChevronRight,
} from 'lucide-react'

type DashboardStats = {
  clients: { total: number; active: number }
  sessions: { today: number; pending_notes: number; this_week: number }
  authorizations: { expiring_soon: number; low_units: number }
  billing: { pending_claims: number; denied_claims: number; ar_total: number }
  compliance: { unsigned_notes: number; overdue_supervisions: number }
}

type RecentActivity = {
  id: string
  type: 'session' | 'note' | 'claim' | 'authorization' | 'compliance'
  description: string
  timestamp: string
  status: 'success' | 'warning' | 'error'
}

type TodaySession = {
  id: number
  client_name: string
  service_code: string
  scheduled_start: string
  scheduled_end: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show' | 'late_cancel'
  note_status: 'not_started' | 'draft' | 'signed' | 'pending_attest' | 'locked'
}

function formatLocalDateLong(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function badgeForSessionStatus(status: TodaySession['status']): string {
  const badges: Record<TodaySession['status'], string> = {
    scheduled: 'bg-blue-50 border border-blue-200 text-blue-800',
    in_progress: 'bg-green-50 border border-green-200 text-green-800',
    completed: 'bg-gray-50 border border-gray-200 text-gray-800',
    cancelled: 'bg-red-50 border border-red-200 text-red-800',
    no_show: 'bg-red-50 border border-red-200 text-red-800',
    late_cancel: 'bg-amber-50 border border-amber-200 text-amber-800',
  }
  return badges[status]
}

function badgeForNoteStatus(status: TodaySession['note_status']): string {
  const badges: Record<TodaySession['note_status'], string> = {
    not_started: 'bg-red-50 border border-red-200 text-red-800',
    draft: 'bg-amber-50 border border-amber-200 text-amber-800',
    signed: 'bg-blue-50 border border-blue-200 text-blue-800',
    pending_attest: 'bg-blue-50 border border-blue-200 text-blue-800',
    locked: 'bg-green-50 border border-green-200 text-green-800',
  }
  return badges[status]
}

export default function DashboardPage() {
  const tenantSlug = resolveTenantSlug()
  const user = useAuthStore((s) => s.user)

  const statsCacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:dashboard:stats`
  }, [tenantSlug])

  const activitiesCacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:dashboard:activities`
  }, [tenantSlug])

  const todayCacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:dashboard:today_sessions`
  }, [tenantSlug])

  const statsQuery = useOfflineFirstQuery<DashboardStats>({
    queryKey: ['dashboard', 'stats'],
    cacheKey: statsCacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<DashboardStats>('/api/v1/dashboard/stats/')
      return res.data
    },
  })

  const activitiesQuery = useOfflineFirstQuery<RecentActivity[]>({
    queryKey: ['dashboard', 'activities'],
    cacheKey: activitiesCacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<RecentActivity[]>('/api/v1/dashboard/activities/')
      return res.data
    },
  })

  const todaySessionsQuery = useOfflineFirstQuery<TodaySession[]>({
    queryKey: ['dashboard', 'today-sessions'],
    cacheKey: todayCacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<TodaySession[]>('/api/v1/dashboard/today-sessions/')
      return res.data
    },
  })

  const stats = statsQuery.data
  const todaySessions = todaySessionsQuery.data ?? []
  const activities = activitiesQuery.data ?? []

  const statCards = useMemo(() => {
    if (!stats || !user) return []

    const cards = [
      {
        name: 'Active Clients',
        value: stats.clients.active,
        icon: Users,
        color: 'bg-blue-600',
        link: '/app/clients?status=active',
        roles: ['admin', 'bcba'],
      },
      {
        name: "Today's Sessions",
        value: stats.sessions.today,
        icon: Calendar,
        color: 'bg-green-600',
        link: '/app/schedule',
        roles: ['admin', 'bcba', 'rbt'],
      },
      {
        name: 'Pending Notes',
        value: stats.sessions.pending_notes,
        icon: FileText,
        color: stats.sessions.pending_notes > 0 ? 'bg-amber-600' : 'bg-gray-500',
        link: '/app/sessions?pending_notes=true',
        roles: ['admin', 'bcba', 'rbt'],
        urgent: stats.sessions.pending_notes > 5,
      },
      {
        name: 'Expiring Auths',
        value: stats.authorizations.expiring_soon,
        icon: AlertTriangle,
        color: stats.authorizations.expiring_soon > 0 ? 'bg-orange-600' : 'bg-gray-500',
        link: '/app/authorizations?filter=expiring',
        roles: ['admin', 'bcba', 'billing'],
        urgent: stats.authorizations.expiring_soon > 0,
      },
      {
        name: 'Low Unit Auths',
        value: stats.authorizations.low_units,
        icon: TrendingUp,
        color: stats.authorizations.low_units > 0 ? 'bg-amber-600' : 'bg-gray-500',
        link: '/app/authorizations?filter=low_units',
        roles: ['admin', 'bcba', 'billing'],
        urgent: stats.authorizations.low_units > 0,
      },
      {
        name: 'Pending Claims',
        value: stats.billing.pending_claims,
        icon: DollarSign,
        color: 'bg-purple-600',
        link: '/app/billing/claims?status=draft',
        roles: ['admin', 'billing'],
      },
      {
        name: 'Denied Claims',
        value: stats.billing.denied_claims,
        icon: AlertTriangle,
        color: stats.billing.denied_claims > 0 ? 'bg-red-600' : 'bg-gray-500',
        link: '/app/billing/denials',
        roles: ['admin', 'billing'],
        urgent: stats.billing.denied_claims > 0,
      },
      {
        name: 'Unsigned Notes',
        value: stats.compliance.unsigned_notes,
        icon: FileText,
        color: stats.compliance.unsigned_notes > 0 ? 'bg-red-600' : 'bg-gray-500',
        link: '/app/notes?filter=pending',
        roles: ['admin', 'bcba', 'rbt'],
        urgent: stats.compliance.unsigned_notes > 0,
      },
    ]

    return cards.filter((c) => c.roles.includes(user.role))
  }, [stats, user])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {getGreeting()}, {user?.first_name}
          </h1>
          <p className="text-gray-600">{formatLocalDateLong(new Date())}</p>
        </div>
        <button className="relative p-2 text-gray-400 hover:text-gray-500" type="button">
          <Bell className="h-6 w-6" />
        </button>
      </div>

      {(statsQuery.isError || activitiesQuery.isError || todaySessionsQuery.isError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(statsQuery.error) ||
            getErrorMessage(activitiesQuery.error) ||
            getErrorMessage(todaySessionsQuery.error)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsQuery.isLoading && !stats ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <div key={idx} className="card p-6">
              <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
              <div className="mt-3 h-8 w-16 bg-gray-200 rounded animate-pulse" />
              <div className="mt-4 h-10 w-10 bg-gray-200 rounded animate-pulse" />
            </div>
          ))
        ) : statCards.length === 0 ? (
          <div className="card p-6 col-span-full text-sm text-gray-600">
            No dashboard cards available for this role.
          </div>
        ) : (
          statCards.map((card) => (
            <Link
              key={card.name}
              to={card.link}
              className={`card p-6 hover:shadow-md transition-shadow ${
                card.urgent ? 'ring-2 ring-red-200' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.name}</p>
                  <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                </div>
                <div className={`${card.color} p-3 rounded-lg`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Today's Sessions</h2>
              <Link to="/app/schedule" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                View schedule
              </Link>
            </div>
            <div className="divide-y divide-gray-200">
              {todaySessionsQuery.isLoading && todaySessions.length === 0 ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <div key={idx} className="h-12 bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              ) : todaySessions.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">No sessions scheduled.</div>
              ) : (
                todaySessions.slice(0, 10).map((session) => (
                  <Link
                    key={session.id}
                    to={`/app/sessions/${session.id}`}
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{session.client_name}</p>
                      <p className="text-sm text-gray-500">
                        {session.scheduled_start} - {session.scheduled_end} | {session.service_code}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeForSessionStatus(session.status)}`}>
                        {session.status.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeForNoteStatus(session.note_status)}`}>
                        {session.note_status.replace('_', ' ')}
                      </span>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Recent Activity</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {activitiesQuery.isLoading && activities.length === 0 ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div key={idx} className="h-10 bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              ) : activities.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">No recent activity.</div>
              ) : (
                activities.slice(0, 8).map((activity) => (
                  <div key={activity.id} className="px-6 py-4 flex items-start gap-3">
                    {activity.status === 'success' ? (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : activity.status === 'warning' ? (
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {activity.timestamp}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-4">
              {user?.role !== 'parent' && (
                <>
                  <Link
                    to="/app/sessions/new"
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <Calendar className="h-8 w-8 text-primary-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">New Session</span>
                  </Link>
                  <Link
                    to="/app/notes?filter=pending"
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <FileText className="h-8 w-8 text-primary-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Pending Notes</span>
                  </Link>
                </>
              )}

              {(user?.role === 'admin' || user?.role === 'bcba') && (
                <Link
                  to="/app/clients/new"
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <Users className="h-8 w-8 text-primary-600 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Add Client</span>
                </Link>
              )}

              {(user?.role === 'admin' || user?.role === 'billing') && (
                <Link
                  to="/app/billing/claims/generate"
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <DollarSign className="h-8 w-8 text-primary-600 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Generate Claims</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
